import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Building2,
  Upload,
  Palette,
  Check,
  Loader2,
  Globe,
  Mail,
  Phone,
  MapPin,
  FileText
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { companyAPI } from "@/services/companyService";
import { useDispatch, useSelector } from "react-redux";
import { updateCompany } from "@/store/slices/authSlice";
import { setBranding, fetchBranding } from "@/store/slices/brandingSlice";
import { RootState, AppDispatch } from "@/store";

export default function OrganizationSettings() {
  const { company } = useAuth();
  const branding = useSelector((state: RootState) => state.branding);
  const { toast } = useToast();
  const dispatch = useDispatch<AppDispatch>();

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    currency: "USD",
    email: "",
    phone: "",
    website: "",
    address: "",
    logo_url: ""
  });

  useEffect(() => {
    if (branding.status === 'idle') {
      dispatch(fetchBranding());
    }
    
    if (!isInitialized && (branding.status === 'succeeded' || branding.lastUpdated)) {
      setFormData({
        name: company?.name || branding.name || "",
        currency: (company as any)?.currency || (branding as any).currency || "USD",
        email: company?.email || branding.email || "",
        phone: company?.phone || branding.phone || "",
        website: (company as any)?.website || branding.website || "",
        address: company?.address || branding.address || "",
        logo_url: (company as any)?.logo_url || branding.logo_url || ""
      });
      setIsInitialized(true);
    }
  }, [branding, dispatch, isInitialized, company]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };


  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // 1. Obtener URL de subida
      const response = await companyAPI.getLogoUploadUrl(file.name, file.type);
      if (response.success) {
        const { uploadUrl, fileUrl } = response.data;
        
        // 2. Subir a S3
        await companyAPI.uploadToS3(uploadUrl, file);
        
        // 3. Actualizar estado local
        setFormData(prev => ({ ...prev, logo_url: fileUrl }));
        
        toast({
          title: "Logo subido",
          description: "El logo se ha cargado correctamente. No olvides guardar los cambios.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error al subir logo",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await companyAPI.updateSettings({
        name: formData.name,
        currency: formData.currency,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        logo_url: formData.logo_url
      });

      if (response.success) {
        // Actualizar Auth (para datos generales persistidos en login)
        dispatch(updateCompany(response.data));
        
        // Actualizar Branding (Single Source of Truth para UI y documentos)
        dispatch(setBranding({
          name: response.data.name,
          currency: response.data.currency,
          email: response.data.email,
          phone: response.data.phone,
          website: response.data.website,
          address: response.data.address,
          logo_url: response.data.logo_url,
        } as any));
        
        toast({
          title: "Configuración guardada",
          description: "Los cambios se han aplicado correctamente y el branding se ha actualizado.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error al guardar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-blue-100 p-2.5 rounded-2xl">
              <Building2 className="h-7 w-7 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Mi Empresa</h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-1">
            Personaliza los datos de tu empresa
          </p>
        </div>
        <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="w-full sm:w-auto bg-slate-900 hover:bg-black text-white rounded-xl shadow-lg font-medium transition-all h-11 px-8"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Guardar Cambios
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6">
            {/* Información General */}
            <Card className="border-slate-200 shadow-sm overflow-hidden">
              <CardHeader className="bg-white border-b border-slate-100 pb-4">
                <CardTitle className="flex items-center text-lg font-semibold text-slate-900">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-lg mr-3">
                    <Building2 className="h-5 w-5" />
                  </div>
                  Información Legal
                </CardTitle>
                <CardDescription>
                  Datos que aparecerán en facturas y documentos oficiales
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre Comercial</Label>
                    <div className="relative">
                        <Input 
                            id="name" 
                            name="name" 
                            value={formData.name} 
                            onChange={handleInputChange}
                            placeholder="Ej: CRM"
                            className="pl-10"
                        />
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="currency">Moneda Principal</Label>
                    <div className="relative">
                        <select
                            id="currency"
                            name="currency"
                            value={formData.currency}
                            onChange={(e: any) => handleInputChange(e)}
                            className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-10"
                        >
                            <option value="USD">Dólar Estadounidense (USD)</option>
                            <option value="EUR">Euro (EUR)</option>
                            <option value="MXN">Peso Mexicano (MXN)</option>
                            <option value="COP">Peso Colombiano (COP)</option>
                        </select>
                        <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="email">Correo Electrónico</Label>
                        <div className="relative">
                            <Input 
                                id="email" 
                                name="email" 
                                value={formData.email} 
                                onChange={handleInputChange}
                                placeholder="Ej: contacto@miempresa.com"
                                className="pl-10" 
                            />
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="phone">Teléfono de Contacto</Label>
                        <div className="relative">
                            <Input 
                                id="phone" 
                                name="phone" 
                                value={formData.phone} 
                                onChange={handleInputChange}
                                placeholder="Ej: +503 2222-2222"
                                className="pl-10" 
                            />
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="address">Dirección Física</Label>
                    <div className="relative">
                        <Input 
                            id="address" 
                            name="address" 
                            value={formData.address} 
                            onChange={handleInputChange}
                            placeholder="Ej: Calle Principal #123, San Salvador"
                            className="pl-10" 
                        />
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                </div>
              </CardContent>
            </Card>

            {/* Branding / Identidad Visual */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="flex items-center text-lg font-semibold text-slate-900">
                  <div className="p-2 bg-purple-50 text-purple-600 rounded-lg mr-3">
                    <Palette className="h-5 w-5" />
                  </div>
                  Tu Logo
                </CardTitle>
                <CardDescription>
                  Sube tu logotipo (Solo se mostrará en cotizaciones PDF)
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4 md:col-span-2">
                    <Label className="text-sm font-semibold">Logotipo de la Empresa</Label>
                    <div className="relative group">
                      <div className="aspect-square w-full max-w-[200px] mx-auto bg-white border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center overflow-hidden transition-all group-hover:border-slate-400">
                        {formData.logo_url ? (
                          <>
                            <img src={formData.logo_url} alt="Logo preview" className="w-full h-full object-contain p-4" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Button variant="secondary" size="sm" className="bg-white/90 hover:bg-white" asChild>
                                    <label className="cursor-pointer">
                                        <Upload className="h-4 w-4 mr-2" />
                                        Cambiar
                                        <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                                    </label>
                                </Button>
                            </div>
                          </>
                        ) : (
                          <label className="flex flex-col items-center justify-center cursor-pointer w-full h-full">
                            {uploading ? (
                                <Loader2 className="h-8 w-8 text-slate-900 animate-spin" />
                            ) : (
                                <>
                                    <div className="p-3 bg-slate-50 text-slate-400 rounded-full mb-3 group-hover:bg-slate-100 group-hover:text-slate-900 transition-colors">
                                        <Upload className="h-6 w-6" />
                                    </div>
                                    <span className="text-sm text-slate-500 group-hover:text-slate-900 font-medium">Subir Logo</span>
                                    <span className="text-[10px] text-slate-400 mt-1">PNG, JPG o SVG (Máx 2MB)</span>
                                </>
                            )}
                            <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">

            <Card className="border-blue-100 bg-blue-50/30">
                <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                            <Loader2 className="h-4 w-4" />
                        </div>
                        <div>
                            <h4 className="text-sm font-semibold text-blue-900">¿Necesitas ayuda?</h4>
                            <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                                Si tienes problemas para configurar tu identidad visual, contacta a soporte para asistencia técnica.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
          </div>
        </div>
      </div>
  );
}
